import React, { useEffect, useState } from "react";
import { message as AntdMessage } from "antd";
import { apiLogin } from "@/apis/auth";
import { useNavigate } from "react-router-dom";
import useLoginStore from "@/stores/loginStore";
import { getUserRoleFromToken } from "@/utils/jwt";

import { AuthLayout } from "@/components/auth-layout";
import { Button } from "@/components/button";
import { Checkbox, CheckboxField } from "@/components/checkbox";
import { Field, Label } from "@/components/fieldset";
import { Heading } from "@/components/heading";
import { Input } from "@/components/input";
import { Strong, Text, TextLink } from "@/components/text";
import { Logo } from "@/components/logo";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, setToken } = useLoginStore();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // 이미 인증된 사용자는 역할에 따라 리다이렉트
  useEffect(() => {
    if (isAuthenticated && token) {
      redirectBasedOnRole(token);
    }
  }, [isAuthenticated, token, navigate]);

  // 사용자 역할에 따라 적절한 페이지로 리다이렉트
  const redirectBasedOnRole = (token: string) => {
    const role = getUserRoleFromToken(token);

    if (role === "student") {
      navigate("/my_exams");
    } else if (role === "admin") {
      navigate("/exam_papers");
    } else {
      // 기본 경로 (역할을 판별할 수 없는 경우)
      navigate("/");
    }
  };

  const validateInputs = (): boolean => {
    let isValid = true;

    // Validate email
    if (!email) {
      setEmailError("이메일을 입력해주세요!");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Validate password
    if (!password) {
      setPasswordError("비밀번호를 입력해주세요!");
      isValid = false;
    } else {
      setPasswordError("");
    }

    return isValid;
  };

  const requestLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiLogin({ email, password });

      if (response.success && response.data?.token) {
        // 토큰 저장
        setToken(response.data.token);
        AntdMessage.success("Login success!");

        // 역할에 따라 리다이렉트
        redirectBasedOnRole(response.data.token);
      }
    } catch (error: any) {
      AntdMessage.error(error.message || "로그인에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form
        onSubmit={requestLogin}
        className="grid w-full max-w-sm grid-cols-1 gap-8"
      >
        <Logo className="h-6 text-zinc-950 dark:text-white forced-colors:text-[CanvasText]" />
        <Heading className="h-6 text-2xl/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white">
          Sign in to your account
        </Heading>

        <Field>
          <Label>Email</Label>
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {emailError && (
            <Text className="text-red-500 text-sm mt-1">{emailError}</Text>
          )}
        </Field>

        <Field>
          <Label>Password</Label>
          <Input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {passwordError && (
            <Text className="text-red-500 text-sm mt-1">{passwordError}</Text>
          )}
        </Field>

        <Button
          type="submit"
          className={`w-full ${loading ? "opacity-70" : ""}`}
        >
          {"Login"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
